import React, { useState, useRef } from 'react';
import Header from './Header';
import BottomNavigation from './BottomNavigation';

function Calls() {
  // Sample call data - in a real app this would come from the story data
  const calls = [
    {
      id: 1,
      contact: "Detective Sarah Collins",
      type: "incoming",
      status: "answered",
      duration: "2:34",
      timestamp: "2024-01-15 14:30",
      hasAnswerphone: false,
      notes: "Case update discussion"
    },
    {
      id: 2,
      contact: "Sophie Renshaw",
      type: "outgoing",
      status: "missed",
      duration: "0:00",
      timestamp: "2024-01-15 13:45",
      hasAnswerphone: true,
      notes: "Left message about new evidence"
    },
    {
      id: 3,
      contact: "Daniel Price",
      type: "incoming",
      status: "missed",
      duration: "0:00",
      timestamp: "2024-01-15 12:20",
      hasAnswerphone: true,
      notes: "Wants to discuss alibi"
    },
    {
      id: 4,
      contact: "PC Sam Carter",
      type: "outgoing",
      status: "answered",
      duration: "1:15",
      timestamp: "2024-01-15 11:30",
      hasAnswerphone: false,
      notes: "Scene investigation report"
    },
    {
      id: 5,
      contact: "Dr. Priya Nair",
      type: "incoming",
      status: "answered",
      duration: "3:42",
      timestamp: "2024-01-15 10:15",
      hasAnswerphone: false,
      notes: "Forensic analysis results"
    }
  ];

  const getStatusIcon = (status, hasAnswerphone) => {
    if (status === 'missed') {
      return hasAnswerphone ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="#8B0000">
          <path d="M19.59 7L12 14.59 6.41 9H11V7H3v8h2v-4.59l7 7 9-9z"/>
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="#8B0000">
          <path d="M19.59 7L12 14.59 6.41 9H11V7H3v8h2v-4.59l7 7 9-9z"/>
        </svg>
      );
    } else if (status === 'answered') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="#008000">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
      );
    }
    return null;
  };

  const getCallTypeIcon = (type) => {
    return type === 'incoming' ? (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="#008000">
        <path d="M20 5.41L18.59 4 7 15.59V9H5v10h10v-2H8.41z"/>
      </svg>
    ) : (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="#8B0000">
        <path d="M9 5v2h6.59L4 18.59 5.41 20 17 8.41V15h2V5z"/>
      </svg>
    );
  };

  const [playingAudio, setPlayingAudio] = useState(null);
  const audioRef = useRef(null);

  const handlePlayAudio = (callId) => {
    if (playingAudio === callId) {
      // Stop the audio if it's already playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingAudio(null);
    } else {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      
      // Start playing the new audio
      if (audioRef.current) {
        audioRef.current.src = '/story/the-missing-hour/audio/answer-message-example.mp3';
        audioRef.current.play();
        setPlayingAudio(callId);
      }
    }
  };

  // Handle audio ended event
  const handleAudioEnded = () => {
    setPlayingAudio(null);
  };

  return (
    <div className="call-page-container">
      <Header />
      
      {/* Hidden audio element for playing answerphone messages */}
      <audio 
        ref={audioRef}
        onEnded={handleAudioEnded}
        preload="metadata"
      />
      
      <div className="page-title-section">
        <h2>Call Log</h2>
      </div>

      <div className="call-list">
        {calls.map(call => (
          <div key={call.id} className={`call-item-simple ${call.status}`}>
            {/* Missed call icon */}
            {call.status === 'missed' && (
              <div className="missed-call-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#8B0000">
                  {/* Phone icon */}
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  {/* Slash overlay to indicate missed */}
                  <path d="M2 22L22 2" stroke="#8B0000" strokeWidth="2" fill="none"/>
                </svg>
              </div>
            )}
            <div className="call-info-simple">
              <div className="call-contact-name">
                {call.contact}
                {call.status === 'missed' && <span className="missed-label"> Missed</span>}
              </div>
              <div className="call-datetime">{call.timestamp}</div>
            </div>
            
            {call.hasAnswerphone && (
              <button 
                className={`play-button ${playingAudio === call.id ? 'playing' : ''}`}
                onClick={() => handlePlayAudio(call.id)}
                title={playingAudio === call.id ? 'Stop message' : 'Play message'}
              >
                {playingAudio === call.id ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                )}
              </button>
            )}
          </div>
        ))}
      </div>
      
      <BottomNavigation showNavigation={true} />
    </div>
  );
}

export default Calls;
